import { IShipmentDriver, IShipmentMotorCarrier } from '../schema'
import { IUserMotorCarrier, TEAM_MEMBER_ROLE_TYPES, IDispatcher, IMotorCarrier } from '../types'

export const getMotorCarrier = (
  userMotorCarriers: IUserMotorCarrier[] = [],
  motorCarrierId: string | number
): IUserMotorCarrier | undefined => {
  return userMotorCarriers?.find(mc => mc?.motorCarrier?.id === motorCarrierId)
}

export const getMotorCarrierProp = <K extends keyof IUserMotorCarrier>(
  userMotorCarriers: IUserMotorCarrier[],
  motorCarrierId: string | number,
  keyName: K
): IUserMotorCarrier[K] | undefined => {
  const mc = getMotorCarrier(userMotorCarriers, motorCarrierId)
  if (!mc) return undefined
  return mc[keyName]
}

export const getCarrierRoleType = (
  userMotorCarriers: IUserMotorCarrier[],
  motorCarrierId: string | number
): TEAM_MEMBER_ROLE_TYPES | undefined => {
  const membership = getMotorCarrierProp(userMotorCarriers, motorCarrierId, 'membership')
  return membership?.role
}

export const isRoleTypeForCarrier = (
  userMotorCarriers: IUserMotorCarrier[],
  motorCarrierId: string | number,
  role: keyof typeof TEAM_MEMBER_ROLE_TYPES
): boolean => {
  return getCarrierRoleType(userMotorCarriers, motorCarrierId) === TEAM_MEMBER_ROLE_TYPES[role]
}

export const findDispatcherById = (dispatchers: IDispatcher[], dispatcherId: string | number) => {
  return dispatchers?.find(d => d.id === dispatcherId)
}

export const findDriverById = (
  drivers: IShipmentDriver[],
  driverId: IShipmentDriver['id']
): IShipmentDriver | undefined => {
  return drivers?.find(d => d.id === driverId)
}

export const findCarrierById = <T extends Pick<IShipmentMotorCarrier, 'id'>>(
  motorCarriers: T[] = [],
  motorCarrierId: string | number
): T | undefined => {
  return motorCarriers?.find(mc => mc.id === motorCarrierId)
}

export const hasCarrierId = (
  motorCarriers: IMotorCarrier[] = [],
  motorCarrierId: string | number
): boolean => {
  return motorCarriers.some(mc => mc.id === motorCarrierId)
}

export const isDispatcherAssociatedToCarrier = (
  dispatcher: IDispatcher,
  motorCarrierId: string | number
): boolean => {
  const { motorCarriers = [] } = dispatcher || {}
  return !!findCarrierById(motorCarriers, motorCarrierId)
}

export const isDriverAssociatedToCarrier = (
  driver: IShipmentDriver,
  motorCarrierId: string | number
): boolean => {
  const { motorCarriers = [] } = driver || {}
  return !!findCarrierById(motorCarriers, motorCarrierId)
}

export const groupItemsByMotorCarrierId = <T extends { motorCarrierId?: number }>(
  items: T[] = [],
  motorCarrierIds: number[] = []
): { associated: T[]; unassociated: T[] } => {
  const associated: T[] = []
  const unassociated: T[] = []

  for (const item of items) {
    if (item?.motorCarrierId && motorCarrierIds.includes(item.motorCarrierId)) {
      associated.push(item)
    } else {
      unassociated.push(item)
    }
  }

  return {
    associated,
    unassociated,
  }
}
